import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import FileUpload from 'Components/FileUpload';
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
// Material UI
import {
    Typography,
    Grid
} from '@mui/material';
import { Alert } from '@mui/material';
// Components
import { Button, Form, Select } from 'Components';
import { useFileUpload } from 'Core/hooks';
import { NotificationManager } from 'Components/Notifications';
import { HasPermission } from 'Core/security';

// Actions
import {
    importAssets,
    openImportAssetsModal,
    resetImportState,
    clearError,
    setFileKey,
    setDelimiter
} from './redux/slice';

const ModalImportAssets = (props) => {
    // Intl
    const { messages } = useIntl();
    const delimiters = [
        { id: ",", name: messages['importAssets_comma']}, 
        { id: ";", name: messages['importAssets_semicolon']}
    ];
    
    // Redux state
    const { 
        openImport, 
        loading, 
        importResponse, 
        error,
    } = useSelector((state) => state.assetsImport);
    const user = useSelector(state => state.securityRedux.user);
   
    
    // Hooks
    const dispatch = useDispatch();
    const formRef = useRef();
    const filesUpload = useFileUpload([]);
    
    // Local state
    const [delimiter, setDelimiterLocal] = useState({
        id: '',
        name: messages['importAssets_selectDelimiter']
    });
    const [disableButton, setDisableButton] = useState(false);
    const [errorMessage, setErrorMessage] = useState({
        message: '',
        type: ''
    });

    const handleClose = useCallback(() => {
        dispatch(openImportAssetsModal(false));
        dispatch(resetImportState());
        setErrorMessage({
            message: '',
            type: ''
        });
        setDisableButton(false);
        setDelimiterLocal({
            id: '',
            name: messages['importAssets_selectDelimiter']
        });
    }, [dispatch, messages]);

    // Handle successful import
    useEffect(() => {
        if (importResponse && openImport) {
            setDisableButton(true);
            NotificationManager.success(
                messages['importAssets_started'],
                messages['importAssets_title'],
                3000,
                null,
                null,
                'success'
            );
            handleClose();
        }
    }, [importResponse, openImport, messages, handleClose]);

    // Handle import errors
    useEffect(() => {
        if (error) {
            setErrorMessage({
                message: error,
                type: 'error'
            });
            setDisableButton(false);
        }
    }, [error]);

    const handleDelimiterChange = useCallback((selectedDelimiter) => {
        setDelimiterLocal(selectedDelimiter);
        dispatch(setDelimiter(selectedDelimiter.id));
    }, [dispatch]);

    const handleImportSubmit = useCallback(() => {
        if (filesUpload.value.length < 1) {
            setErrorMessage({
                message: messages['importAssets_error_required'],
                type: 'error'
            });
            return false;
        }

        if (!delimiter.id) {
            setErrorMessage({
                message: messages['importAssets_error_delimiter'],
                type: 'error'
            });
            return false;
        }

        // Clear any existing errors
        dispatch(clearError());
        setErrorMessage({ message: '', type: '' });
        
        // Start import with selected delimiter
        dispatch(setFileKey(filesUpload.value[0].fileKey));
        dispatch(importAssets({ 
            fileKey: filesUpload.value[0].fileKey, 
            delimiter: delimiter.id 
        }));
    }, [filesUpload.value, delimiter.id, dispatch, messages]);

    const downloadTemplate = useCallback((e) => {
        e.preventDefault();
        
        const delimiterSelected = delimiter?.id || ',';
        
        // Base CSV headers for assets
        let csvHeaders = [
            messages["importAssets_template_id"],
            messages["importAssets_template_label"],
            messages["importAssets_template_tags"],
            messages["importAssets_template_type"],
            messages["importAssets_template_subtype"],
            messages["importAssets_template_externalId"],
            messages["importAssets_template_InspectionDeviceType"],
            messages["importAssets_template_vin"],
            messages["importAssets_template_make"],
            messages["importAssets_template_model"],
            messages["importAssets_template_year"],
            messages["importAssets_template_status"],
            messages["importAssets_template_maintenanceCompany"],
            messages["importAssets_template_registrationCountry"],
            messages["importAssets_template_registrationState"],
            messages["importAssets_template_registrationExpiration"],
            messages["importAssets_template_engineSerialNumber"],
            messages["importAssets_template_chassisSerialNumber"],
            messages["importAssets_template_licensePlate"]
        ];
        
        // Add conditional headers based on permissions
        if (HasPermission(user, 'evir', [295])) {
            csvHeaders.push(
                messages["importAssets_template_InspectionTagId"]
            );
        }
        
        if (HasPermission(user, 'zlogs', [294])) {
            csvHeaders.push(messages["importAssets_template_powerUnitNumber"]);
        }
        
        const csvContent = csvHeaders.join(delimiterSelected) + '\n';
        
        const blob = new Blob([csvContent], {
            type: 'text/csv;encoding:utf-8;charset=UTF-8',
        });
        
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.setAttribute('download', 'assets_template.csv');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [delimiter, messages, user]);

    return (
        <Fragment>
            <Modal
                title={messages['importAssets_title']}
                open={openImport}
                loading={loading}
                position="right"
                size="small"
                handleClose={handleClose}
            >
                <ModalBody>
                    <div style={{ padding: '18px 24px 24px 24px', height: 650 }}>
                        {errorMessage.message && (
                            <Alert 
                                severity={errorMessage.type} 
                                onClose={() => setErrorMessage({ message: '', type: '' })}
                            >
                                {(() => {
                                    const errorCode = errorMessage.message.includes('_') 
                                        ? errorMessage.message.split('_')[0] 
                                        : null;
                                    return errorCode && messages[`assets_import_error_${errorCode}`]
                                        ? messages[`assets_import_error_${errorCode}`]
                                        : errorMessage.message;
                                })()}
                            </Alert>
                        )}
                        
                        <Form
                            ref={formRef}
                            onValidSubmit={handleImportSubmit}
                        >
                            <div style={{ padding: 24 }}>
                                <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Grid item sm={12}>
                                        <div style={{ marginBottom: 16, backgroundColor: '#fff3cd', padding: 16, borderRadius: 4, fontWeight: 500 }}>
                                            {messages['asset_import_instructions']}
                                        </div>
                                        <Button
                                            type="button"
                                            className="btn-blue"
                                            style={{ width: '100%', marginBottom: 16 }}
                                            onClick={downloadTemplate}
                                        >
                                            {messages['importAssets_downloadTemplate']}
                                        </Button>
                                    </Grid>
                                    
                                    <Grid item sm={12}>
                                        <Select
                                            id="delimiter"
                                            label={messages['importAssets_delimiter']}
                                            placeholder={messages['importAssets_selectDelimiter']}
                                            valueField="id"
                                            textField="name"
                                            options={delimiters}
                                            value={delimiter}
                                            required
                                            onChange={handleDelimiterChange}
                                        />
                                    </Grid>
                                    
                                    <Grid item sm={12}>
                                        <FileUpload
                                            {...filesUpload}
                                            label={`${messages['importAssets_file']} (CSV)`}
                                            placeholder={messages['importAssets_chooseFile']}
                                            types=".csv"
                                            helpText={messages['importAssets_support_message']}
                                            maxSize={5}
                                            disabled={filesUpload?.value?.length > 0 || !delimiter?.id}
                                            maxSizeMessage={`${messages['importAssets_max']}: 5MB`}
                                        />
                                        <small className="form-text text-muted">
                                            {messages['importAssets_maxSizeFile']}
                                        </small>
                                    </Grid>
                                </Grid>
                            </div>
                        </Form>
                    </div>
                </ModalBody>
                
                <ModalFooter style={{ bottom: 50, textAlign: 'right' }}>
                    <Button
                        onClick={handleClose}
                        disabled={disableButton}
                        className="btn-cancel"
                    >
                        {messages['importAssets_cancel']}
                    </Button>

                    <Button
                        onClick={(e) => {
                            if (e) e.preventDefault();
                            formRef.current.submit();
                        }}
                        disabled={disableButton || filesUpload?.value?.length === 0 || !delimiter?.id}
                        className="btn-blue"
                    >
                        {messages['importAssets_button']}
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default ModalImportAssets;